import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
  FormControlLabel,
  Switch,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  BugReport as BugReportIcon,
  Assignment as AssignmentIcon,
  Science as ScienceIcon,
  Cancel as CancelIcon,
  Note as NoteIcon
} from '@mui/icons-material';
import { visitsService, CreateAddendumData } from '@services/visitsService';

interface AddAddendumDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  visitId: string;
}

const AddAddendumDialog: React.FC<AddAddendumDialogProps> = ({
  open,
  onClose,
  onSuccess,
  visitId,
}) => {
  const [formData, setFormData] = useState<CreateAddendumData>({
    reason: '',
    content: '',
    type: 'other',
    notify_owner: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addendumTypes = [
    {
      value: 'correction',
      label: 'Corectie',
      description: 'Corectarea unei informatii gresite din consultatie',
      icon: <BugReportIcon />
    },
    {
      value: 'completion',
      label: 'Completare',
      description: 'Completarea informatiilor lipsa din consultatie',
      icon: <AssignmentIcon />
    },
    {
      value: 'laboratory_result',
      label: 'Rezultat laborator',
      description: 'Adaugarea unui rezultat de laborator primit ulterior',
      icon: <ScienceIcon />
    },
    {
      value: 'prescription_void',
      label: 'Anulare reteta',
      description: 'Anularea unei retete prescrise anterior',
      icon: <CancelIcon />
    },
    {
      value: 'other',
      label: 'Altele',
      description: 'Alte tipuri de modificari sau completari',
      icon: <NoteIcon />
    }
  ];

  const handleInputChange = (field: keyof CreateAddendumData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.reason?.trim()) {
      setError('Motivul addendum-ului este obligatoriu');
      return false;
    }
    if (!formData.content.trim()) {
      setError('Continutul addendum-ului este obligatoriu');
      return false;
    }
    if (formData.content.trim().length < 10) {
      setError('Continutul trebuie sa aiba cel putin 10 caractere');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError(null);

      await visitsService.addAddendum(visitId, formData);

      setFormData({
        reason: '',
        content: '',
        type: 'other',
        notify_owner: true,
      });

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error adding addendum:', error);
      setError(error.response?.data?.message || 'Eroare la adaugarea addendum-ului');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        reason: '',
        content: '',
        type: 'other',
        notify_owner: true,
      });
      setError(null);
      onClose();
    }
  };

  const selectedType = addendumTypes.find(type => type.value === formData.type);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AddIcon />
        Adauga Addendum
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Addendum-urile</strong> sunt modificari sau completari adaugate la o consultatie blocata.
              Acestea sunt vizibile pentru pacient si sunt incluse in istoricul medical.
            </Typography>
          </Alert>
        </Box>

        <FormControl fullWidth margin="normal">
          <InputLabel>Tip addendum</InputLabel>
          <Select
            value={formData.type}
            onChange={(e) => handleInputChange('type', e.target.value)}
            label="Tip addendum"
          >
            {addendumTypes.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {type.icon}
                  <Box>
                    <Typography variant="body1">{type.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {type.description}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedType && (
          <Box sx={{ mb: 2 }}>
            <Chip
              icon={selectedType.icon}
              label={selectedType.label}
              color="primary"
              variant="outlined"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {selectedType.description}
            </Typography>
          </Box>
        )}

        <TextField
          fullWidth
          margin="normal"
          label="Motivul addendum-ului"
          value={formData.reason}
          onChange={(e) => handleInputChange('reason', e.target.value)}
          placeholder="Ex: Corectare diagnostic, Rezultat laborator primit ulterior, etc."
          required
          multiline
          rows={2}
        />

        <TextField
          fullWidth
          margin="normal"
          label="Continutul addendum-ului"
          value={formData.content}
          onChange={(e) => handleInputChange('content', e.target.value)}
          placeholder="Descrie in detaliu modificarea sau completarea..."
          required
          multiline
          rows={6}
          helperText={`${formData.content.length} caractere (minimum 10)`}
        />

        <Divider sx={{ my: 2 }} />

        <FormControlLabel
          control={
            <Switch
              checked={formData.notify_owner}
              onChange={(e) => handleInputChange('notify_owner', e.target.checked)}
            />
          }
          label="Notifica proprietarul animalului"
        />

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Daca este activata, proprietarul va fi notificat despre acest addendum.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={handleClose} 
          disabled={loading}
        >
          Anuleaza
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
        >
          {loading ? 'Se adauga...' : 'Adauga Addendum'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddAddendumDialog;
